<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import useFlowNavigation from '@Use/useFlowNavigation';
import { useFlowSteps } from '@Use/useFlowSteps';
import useDashboardContext from '@Use/useDashboardContext';
import { isEmdSendType } from '@Enums/sendType.enum';

// import components
import { DetailsLayout, FlowLayout, IllustrationSendPriming } from '@Shared';
import WireInstructionsContent from '@Components/send/WireInstructionsContent.vue';

const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestDetailsStore = useRequestDetailsStore();
const { goBackInFlow } = useFlowNavigation();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { isDashboardContext, isLoadingContext } = useDashboardContext();

const isLoading = ref(true);
const errorMessage = ref<string | null>(null);
const isPageLoading = computed(() => isLoading.value || isLoadingContext.value);

// DS-3309: an EMD wire-instructions-only request surfaces here with sendType 'Emd'.
// Give the dashboard banner EMD-specific framing so the buyer knows the
// instructions are for their earnest money deposit, not a generic closing payment.
const isEmd = computed(() => isEmdSendType(requestDetailsStore.requestDetails?.sendType));
const detailsTitle = computed(() =>
    isEmd.value ? 'Your earnest money deposit instructions' : 'Your secure wire instructions',
);

onMounted(async () => {
    isLoading.value = true;
    const result = await requestDetailsStore.fetchRequestDetails();
    if (!result?.success) {
        errorMessage.value = 'Failed to load wire instructions. Please refresh and try again.';
    }
    isLoading.value = false;
});
</script>

<template>
    <app-page-loader v-if="isPageLoading" />

    <details-layout
        v-else-if="isDashboardContext"
        :title="detailsTitle"
        illustration-align="bottom"
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">

        <template #illustration>
            <illustration-send-priming />
        </template>

        <wire-instructions-content
            :is-dashboard-context="isDashboardContext"
            :error-message="errorMessage"
            :show-title="false" />

    </details-layout>

    <flow-layout
        v-else
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        @back-click="goBackInFlow">

        <wire-instructions-content
            :is-dashboard-context="isDashboardContext"
            :error-message="errorMessage" />

    </flow-layout>
</template>
